import { connect } from 'react-redux';
import { goBack, push } from 'redux-first-history';
import { formatDate, formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import TermLengthPage from './component';
import { PROPERTY_STATUS } from '@rental-manager/constants/OnboardingConstants';
import {
  DEFAULT_ERROR,
  PAYMENT_SETTINGS_UPDATED,
  STOP_PAYMENTS_SUCCESS,
} from '@rental-manager/constants/NotificationConstants';
import { getFeaturePermissions, getLandlordStatus, stopPayments } from '@rental-manager/actions/requests';
import { acceptTerms, savePaymentSettings } from '@rental-manager/actions/OnboardingActions';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import paths from '@rental-manager/pathsManifest';
import HTTP_STATUS from '@common/constants/HttpConstants';
import type { ErrorInterface } from '@common/types';
import { logError } from '@zg-rentals/log-error';
import type { UpdateRentTermsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { UpdateRentTermsWebApiHandler_updateRentTerms } from '@zg-rentals/ts-rent-payment-client';
import type { CreateRentTermsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { CreateRentTermsWebApiHandler_createRentTerms } from '@zg-rentals/ts-rent-payment-client';

const mapStateToProps = (state, { propertyId, location, fromDashboard }) => {
  const fromDueDatePage = location?.state?.fromWhere === paths.dashboard.editDueDatePage;
  const { paymentSettings, leasesForProperty, leaseId, encodedLeaseId } =
    state.payments.landlord.onboarding[propertyId];
  const { currentTerm } = state.payments.landlord.property;
  const propertyStatus = currentTerm.propertyStatus;
  const {
    firstDueDate,
    numberOfPayments,
    monthToMonth,
    monthlyRent,
    editMode,
    requestStatus,
    paymentDayOfTheMonth,
    editDayOfTheMonth,
  } = paymentSettings;
  const numberOfPaymentsUnformatted = fromDashboard ? currentTerm.numberOfPayments : numberOfPayments;
  const {
    startDate: startDateFromLease,
    endDate: endDateFromLease,
    paymentsEncodedLeaseId,
  } = leasesForProperty[leaseId] || {};

  return {
    showOneTimePayment: fromDashboard,
    firstDueDate: fromDashboard ? currentTerm.firstDueDate : firstDueDate,
    numberOfPayments: numberOfPaymentsUnformatted && Number(numberOfPaymentsUnformatted),
    monthToMonth: fromDashboard ? currentTerm.monthToMonth : monthToMonth,
    monthlyRent: fromDashboard ? currentTerm.amount : monthlyRent,
    editDayOfTheMonth,
    paymentDayOfTheMonth:
      fromDashboard && !fromDueDatePage ? Number(formatDate(currentTerm.firstDueDate, 'd')) : paymentDayOfTheMonth,
    requestStatus,
    editMode,
    rentTermsKey: currentTerm?.rentTermsKey ? currentTerm.rentTermsKey : '',
    propertyStatus,
    config: state.payments.landlord.config,
    startDateFromLease,
    endDateFromLease,
    encodedLeaseId,
    paymentsEncodedLeaseId,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
  };
};

export const mapDispatchToProps = (dispatch, { baseUrl, propertyId, fromDashboard }: Record<string, any>) => ({
  requestStopPayments: async (rentTermsKey) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Request to create a manual payment
      const stopPaymentsResponse = await dispatch(stopPayments({ propertyId, rentTermsKey }));
      await checkRequestActionSuccess(stopPaymentsResponse);

      // Request the latest landlord status
      const landlordStatusResponse = await dispatch(getLandlordStatus({ propertyId }));
      await checkRequestActionSuccess(landlordStatusResponse);

      // Navigate to feedback page
      await dispatch(push(baseUrl + paths.dashboard.stopPaymentsFeedbackPage));

      // Show a success banner
      await dispatch(showModalNotification(STOP_PAYMENTS_SUCCESS));

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onSubmit: (
    paymentSettings: Record<string, any>,
    editMode: boolean,
    rentTermsKey: string,
    propertyStatus: string,
    encodedLeaseId: string,
    paymentsEncodedLeaseId: string,
  ) => {
    return Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(acceptTerms({ propertyId })))
      .then(() => dispatch(savePaymentSettings({ propertyId, paymentSettings })))
      .then(async () => {
        let { firstDueDate, monthToMonth } = paymentSettings;
        const { monthlyRent, numberOfPayments, paymentDayOfTheMonth } = paymentSettings;
        const rentTermsExist: boolean =
          propertyStatus === PROPERTY_STATUS.RENT_TERMS_SET || propertyStatus === PROPERTY_STATUS.TENANT_INFO_SET;
        const dueLastDayOfMonth = paymentDayOfTheMonth === 31;
        const amount = parseInt(monthlyRent, 10);
        const encodedLeaseIdForPayload = paymentsEncodedLeaseId ? encodedLeaseId : '';
        monthToMonth = monthToMonth ? true : false;

        if (editMode || rentTermsExist) {
          const updateRentTermsPayload: UpdateRentTermsWebApiRequestDto = {
            resourceId: propertyId,
            rentTermsKey,
            amount,
            firstDueDate: formatYMDStringToTimestamp(firstDueDate),
            monthToMonth,
            numberOfPayments,
            dueLastDayOfMonth,
            encodedLeaseId: encodedLeaseIdForPayload,
            paymentsEncodedLeaseId,
          };
          return UpdateRentTermsWebApiHandler_updateRentTerms(updateRentTermsPayload);
        }

        firstDueDate = formatDate(firstDueDate, 'yyyy-MM-dd');
        const createRentTermsPayload: CreateRentTermsWebApiRequestDto = {
          resourceId: propertyId,
          amount,
          firstDueDate: formatYMDStringToTimestamp(firstDueDate),
          monthToMonth,
          numberOfPayments,
          dueLastDayOfMonth,
          encodedLeaseId: encodedLeaseIdForPayload,
          paymentsEncodedLeaseId,
        };
        return CreateRentTermsWebApiHandler_createRentTerms(createRentTermsPayload);
      })
      .then(() => dispatch(getFeaturePermissions({ propertyId })))
      .then(() => dispatch(getLandlordStatus({ propertyId })))
      .then(() => {
        if (editMode) {
          return dispatch(push(baseUrl + paths.onboarding.confirmationPage));
        } else {
          return dispatch(push(baseUrl + paths.onboarding.moveInCostsPage));
        }
      })
      .then(() => {
        if (editMode) {
          return dispatch(showModalNotification(PAYMENT_SETTINGS_UPDATED));
        }
      })
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      });
  },
  onSubmitFromDashboard: (paymentSettings, rentTermsKey: string) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(savePaymentSettings({ propertyId, paymentSettings })))
      .then(() => {
        const paymentLength = paymentSettings.monthToMonth
          ? { monthToMonth: true }
          : {
              numberOfPayments: paymentSettings.numberOfPayments,
            };

        const updateRentTermsPayload: UpdateRentTermsWebApiRequestDto = {
          resourceId: propertyId,
          rentTermsKey,
          amount: Number(paymentSettings.monthlyRent),
          firstDueDate: formatYMDStringToTimestamp(formatDate(paymentSettings.firstDueDate, 'yyyy-MM-dd 00:00')),
          ...paymentLength,
        };

        return UpdateRentTermsWebApiHandler_updateRentTerms(updateRentTermsPayload);
      })
      .then(() => dispatch(push(baseUrl)))
      .then(() => dispatch(getLandlordStatus({ propertyId })))
      .then(() => dispatch(showPageNotification(PAYMENT_SETTINGS_UPDATED)))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  onCancel: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const payload = {
          propertyId,
          paymentSettings: {
            editDayOfTheMonth: null,
          },
        };
        dispatch(savePaymentSettings(payload));
      })
      .then(() => {
        const newUrl = fromDashboard ? baseUrl : baseUrl + paths.onboarding.confirmationPage;
        return dispatch(push(newUrl));
      }),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(goBack())),
  onClose: (paymentSettings) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(savePaymentSettings({ propertyId, paymentSettings })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup))),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps, { fromDashboard }) => ({
  ...stateProps,
  ...dispatchProps,
  onSubmit: (paymentSettings) =>
    fromDashboard
      ? dispatchProps.onSubmitFromDashboard(paymentSettings, stateProps.rentTermsKey)
      : dispatchProps.onSubmit(
          paymentSettings,
          stateProps.editMode,
          stateProps.rentTermsKey,
          stateProps.propertyStatus,
          stateProps.encodedLeaseId,
          stateProps.paymentsEncodedLeaseId,
        ),
  onClose: () => dispatchProps.onClose(stateProps.editMode),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(TermLengthPage);
